import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Inbox as InboxIcon, Loader2, UserPlus, UserX, RefreshCw,
  Mail, Plane, Repeat, Calendar,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader } from "@/components/host/hostUi";

const currencySymbol = (c) => ({ USD: "$", EUR: "€", GBP: "£" }[(c || "USD").toUpperCase()] || "$");

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
};

const KIND_META = {
  booking: { icon: UserPlus, label: "New booking", colour: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10" },
  season_pass: { icon: UserPlus, label: "Season pass", colour: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10" },
  trip_booking: { icon: UserPlus, label: "Trip booking", colour: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10" },
  cancellation: { icon: UserX, label: "Cancellation", colour: "text-red-600 dark:text-red-400 bg-red-500/10" },
  kick: { icon: UserX, label: "Removed attendee", colour: "text-red-600 dark:text-red-400 bg-red-500/10" },
  refund: { icon: RefreshCw, label: "Wallet credit", colour: "text-blue-600 dark:text-blue-400 bg-blue-500/10" },
  message: { icon: Mail, label: "Email broadcast", colour: "text-fuchsia-600 dark:text-fuchsia-400 bg-fuchsia-500/10" },
};

const TYPE_ICON = { game: Calendar, series: Repeat, trip: Plane };

const FILTERS = [
  { id: "all", label: "All" },
  { id: "bookings", label: "Bookings" },
  { id: "cancellations", label: "Cancellations" },
  { id: "actions", label: "My actions" },
];

const HostInboxPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/inbox?limit=100`, { headers });
      if (!res.ok) {
        toast.error("Couldn't load inbox");
        return;
      }
      setItems(await res.json());
    } catch (_) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "bookings") return items.filter((i) => ["booking", "season_pass", "trip_booking"].includes(i.kind));
    if (filter === "cancellations") return items.filter((i) => i.kind === "cancellation");
    if (filter === "actions") return items.filter((i) => ["kick", "refund", "message"].includes(i.kind));
    return items;
  }, [items, filter]);

  return (
    <HostLayout>
      <div data-testid="host-inbox-page">
        <HostPageHeader
          icon={InboxIcon}
          title="Inbox"
          subtitle="Every booking, cancellation and action across your events."
        />

        {/* Filter strip */}
        <div className="flex items-center gap-1 mb-4 overflow-x-auto" data-testid="inbox-filter-strip">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${active ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                data-testid={`inbox-filter-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="py-12 flex items-center justify-center text-muted-foreground gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <Card className="p-8 text-center" data-testid="inbox-empty">
            <InboxIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="font-medium">Nothing to show</p>
            <p className="text-xs text-muted-foreground mt-1">Your inbox lights up when people book, cancel or message you.</p>
          </Card>
        )}

        {!loading && filtered.length > 0 && (
          <Card className="divide-y divide-border" data-testid="inbox-list">
            {filtered.map((it, idx) => {
              const meta = KIND_META[it.kind] || { icon: InboxIcon, label: it.kind, colour: "bg-muted text-muted-foreground" };
              const TypeIcon = TYPE_ICON[it.event_type] || Calendar;
              const Icon = meta.icon;
              const handleClick = () => {
                if (it.event_type && it.event_id) {
                  navigate(`/host/manage/${it.event_type}/${it.event_id}`);
                }
              };
              return (
                <button
                  key={`${it.kind}-${it.event_id}-${it.user_id || idx}-${it.at}`}
                  onClick={handleClick}
                  className="w-full p-4 flex items-start gap-3 text-left hover:bg-muted/30 transition-colors"
                  data-testid={`inbox-row-${idx}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${meta.colour}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">
                        {meta.label}
                        {it.amount > 0 && (
                          <span className="text-muted-foreground ml-2 text-xs">
                            {currencySymbol(it.currency)}{Number(it.amount).toFixed(2)}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground shrink-0">{formatDate(it.at)}</p>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {it.user_name && <span>{it.user_name}</span>}
                      {it.user_name && (it.event_title || it.subject) && <span> · </span>}
                      {it.subject ? (
                        <span>{`"${it.subject}" · ${it.recipients || 0} recipient${it.recipients === 1 ? "" : "s"}`}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <TypeIcon className="w-3 h-3" />
                          {it.event_title || it.event_id}
                        </span>
                      )}
                    </p>
                  </div>
                </button>
              );
            })}
          </Card>
        )}
      </div>
    </HostLayout>
  );
};

export default HostInboxPage;
